import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, CheckCheck, Inbox } from "lucide-react";
import { axios, API, logError } from "../lib/api";

const POLL_MS = 30000;

/**
 * In-app notification bell — appears in the desktop header for signed-in users.
 *
 * - Polls `GET /api/notifications/inbox` every 30 s.
 * - Shows a red badge with the unread count (capped at 9+).
 * - Click → dropdown showing latest 50 notifications, newest first.
 * - Click a row → marks read + navigates to its `href`.
 * - "Mark all as read" button flips everything in a single call.
 */
export const NotificationsBell = ({ user }) => {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/notifications/inbox`);
      setItems(r.data?.items || []);
      setUnread(r.data?.unread || 0);
    } catch (err) {
      if (err?.response?.status !== 401) logError("notifications-inbox", err);
    }
  }, []);

  // Poll while signed in.
  useEffect(() => {
    if (!user) {
      setItems([]); setUnread(0); return undefined;
    }
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [user, load]);

  // Close dropdown on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const handleRowClick = async (n) => {
    setOpen(false);
    try {
      if (!n.read) {
        await axios.post(`${API}/notifications/inbox/${n.id}/read`);
        setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
        setUnread((u) => Math.max(0, u - 1));
      }
    } catch (err) {
      logError("notifications-mark-read", err);
    }
    if (n.href) navigate(n.href);
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/inbox/mark-all-read`);
      setItems((prev) => prev.map((x) => ({ ...x, read: true })));
      setUnread(0);
    } catch (err) {
      logError("notifications-mark-all", err);
    }
  };

  if (!user) return null;

  return (
    <div className="relative" ref={dropdownRef} data-testid="notifications-bell">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 hover:text-[#FF6B6B]"
        aria-label={`Notifications${unread > 0 ? ` (${unread} unread)` : ""}`}
        data-testid="notifications-bell-button"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 bg-[#FF6B6B] text-white text-[10px] font-black w-5 h-5 rounded-full border-2 border-gray-900 flex items-center justify-center"
            data-testid="notifications-unread-badge"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 bg-white border-2 border-gray-900 rounded-2xl shadow-[6px_6px_0px_0px_rgba(17,24,39,1)] overflow-hidden z-50"
          data-testid="notifications-dropdown"
        >
          <header className="flex items-center justify-between px-4 py-3 border-b-2 border-gray-900 bg-[#FFD93D]">
            <h3 className="font-black text-sm uppercase tracking-wide">Notifications</h3>
            {unread > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                className="text-xs font-bold inline-flex items-center gap-1 hover:underline"
                data-testid="notifications-mark-all"
              >
                <CheckCheck className="w-3.5 h-3.5" /> Mark all read
              </button>
            )}
          </header>
          <NotificationList items={items} onRowClick={handleRowClick} />
        </div>
      )}
    </div>
  );
};

const NotificationList = ({ items, onRowClick }) => {
  if (!items.length) {
    return (
      <div className="p-8 text-center text-sm text-gray-500" data-testid="notifications-empty">
        <Inbox className="w-8 h-8 mx-auto mb-2 text-gray-300" />
        You're all caught up.
      </div>
    );
  }
  return (
    <ul className="max-h-96 overflow-y-auto divide-y divide-gray-100">
      {items.map((n) => (
        <li key={n.id}>
          <button
            type="button"
            onClick={() => onRowClick(n)}
            className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
              !n.read ? "bg-[#FFF6C7]/40" : ""
            }`}
            data-testid={`notification-row-${n.id}`}
          >
            <div className="flex items-start gap-2">
              {!n.read && (
                <span className="w-2 h-2 mt-1.5 rounded-full bg-[#FF6B6B] flex-shrink-0" aria-hidden="true" />
              )}
              {n.read && (
                <span className="w-2 h-2 mt-1.5 flex-shrink-0" aria-hidden="true">
                  <Check className="w-2 h-2 text-gray-300" />
                </span>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm truncate">{n.title}</p>
                <p className="text-xs text-gray-600 line-clamp-2 mt-0.5">{n.body}</p>
                <p className="text-[10px] text-gray-400 mt-1">
                  {new Date(n.created_at).toLocaleString()}
                </p>
              </div>
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
};

export default NotificationsBell;
